<template>
  <h-dialog v-model="modalOpened" @close="dialogClosed" ref="dialog" :title="$t('components.consumer_gift_card_send_by_email_modal.modal_title')">
    <fragment v-if="emailNeedResendConfirmation">
      <p>
        {{ $t('components.consumer_gift_card_send_by_email_modal.resend_message') }}
      </p>
    </fragment>

    <h-form v-else ref="form" v-model="valid">
      <h-text-field
        :disabled="success"
        autofocus
        v-model="sender_name"
        :persistent-hint="true"
        :hint="$t('components.consumer_gift_card_send_by_email_modal.hint_sender_name')"
        :label="$t('components.consumer_gift_card_send_by_email_modal.label_sender_name')"
        :placeholder="$t('components.consumer_gift_card_send_by_email_modal.placeholder_sender_name')"
        :persistent-placeholder="true"
        :rules="senderNameRules"
        maxlength="50"
      />

      <h-text-field
        :disabled="success"
        v-model="recipient_name"
        maxlength="50"
        :persistent-hint="true"
        :hint="$t('components.consumer_gift_card_send_by_email_modal.hint_name')"
        :label="$t('components.consumer_gift_card_send_by_email_modal.label_name')"
        :placeholder="$t('components.consumer_gift_card_send_by_email_modal.placeholder_name')"
        :persistent-placeholder="true"
        :rules="nameRules"
      />

      <h-text-field
        :disabled="success"
        v-model="recipient_email"
        maxlength="255"
        :label="$t('components.consumer_gift_card_send_by_email_modal.label_email')"
        :placeholder="$t('components.consumer_gift_card_send_by_email_modal.placeholder_email')"
        :persistent-placeholder="true"
        :rules="emailRules"
      />

      <h-textarea
        :disabled="success"
        v-model="personalized_message"
        :label="$t('components.consumer_gift_card_send_by_email_modal.label_personalized_message')"
        :placeholder="$t('components.consumer_gift_card_send_by_email_modal.placeholder_personalized_message')"
        persistent-hint
        persistent-placeholder
        height="140"
        maxlength="150"
        :rules="messageRules"
        counter
      />

      <h-checkbox class="checkbox-cc" top-align :disabled="success" :label="$t('components.consumer_gift_card_send_by_email_modal.add_me_cc_label')" v-model="add_user_email_in_cc" />

      <v-radio-group v-if="viewStatus !== 'success'" v-model="language" mandatory>
        <p>{{ $t('components.consumer_gift_card_send_by_email_modal.language_radios.title') }}</p>

        <v-radio class="language-radios" :disabled="success" v-for="(option, index) in languageOptions" :key="index" :value="option.value">
          <template v-slot:label>
            {{ $t(`components.consumer_gift_card_send_by_email_modal.language_radios.${option.label_key}.label`) }}
          </template>
        </v-radio>
      </v-radio-group>

      <p v-if="viewStatus !== 'success'">{{ $t('components.consumer_gift_card_send_by_email_modal.modal_text_1') }}</p>

      <hello-form-error />

      <div v-if="viewStatus === 'success'">
        <div class="success-box">
          <v-icon class="py-1" size="28" color="success">mdi-check-circle-outline</v-icon>
          <p>{{ $t('components.consumer_gift_card_send_by_email_modal.success_message') }}</p>
        </div>
      </div>
    </h-form>

    <template #action-footer>
      <fragment v-if="emailNeedResendConfirmation">
        <h-btn @click="confirmResend" large>{{ $t('components.consumer_gift_card_send_by_email_modal.resend_continue') }}</h-btn>
        <h-btn @click="close" outlined inverted color="primary" large>{{ $t('components.consumer_gift_card_send_by_email_modal.resend_cancel') }}</h-btn>
      </fragment>

      <fragment v-else-if="viewStatus === 'pending'">
        <h-btn @click="submit" large :disabled="!valid" :loading="working">{{ $t('components.consumer_gift_card_send_by_email_modal.send_action') }}</h-btn>
        <h-btn @click="close" :disabled="working" large outlined inverted color="primary">{{ $t('components.consumer_gift_card_send_by_email_modal.cancel_action') }}</h-btn>
      </fragment>

      <fragment v-else-if="viewStatus === 'success'">
        <h-btn @click="close" :disabled="working" large color="primary">{{ $t('components.consumer_gift_card_send_by_email_modal.success_close_button') }}</h-btn>
      </fragment>
    </template>
  </h-dialog>
</template>

<script>
import safeExecute from '@/composables/safe-execute';
import { validateInvalidEmail, validateMaxLength, validateMaxNumLines, validateRequired } from '@/composables/validations';
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'consumer-gift-card-send-by-email-modal',
  setup(props, { root }) {
    const { executeForm, execute, working } = safeExecute(root.$store);
    return {
      execute,
      executeForm,
      working,
      validateRequired,
      validateMaxLength,
      validateMaxNumLines,
      validateInvalidEmail,
    };
  },
  props: {
    voucherId: {
      type: String,
    },
    activationCodeId: {
      type: String,
    },
  },
  data() {
    return {
      modalOpened: false,

      success: null,
      valid: false,

      send_email_status: null,
      resend_confirmed: false,

      activation_code_recipient_loaded: false,

      add_user_email_in_cc: true,
      recipient_name: null,
      recipient_email: null,
      sender_name: null,
      personalized_message: '',
      language: null,

      nameRules: [this.validateRequired('name'), this.validateMaxLength(50)],
      senderNameRules: [this.validateRequired('sender_name'), this.validateMaxLength(50)],
      emailRules: [this.validateRequired('email'), this.validateInvalidEmail(), this.validateMaxLength(255)],
      messageRules: [this.validateMaxLength(150), this.validateMaxNumLines(6)],
      languageOptions: [
        { label_key: 'french', value: 'fr' },
        { label_key: 'english', value: 'en' },
      ],
    };
  },
  computed: {
    ...mapGetters('ui', ['currentLocale']),
    viewStatus() {
      if (this.success === false) {
        return 'error';
      }
      if (this.success === true) {
        return 'success';
      }
      return 'pending';
    },

    emailNeedResendConfirmation() {
      return this.send_email_status === 'sent' && !this.resend_confirmed;
    },
  },
  methods: {
    ...mapActions('error', ['clearError']),
    async openModal() {
      await this.loadActivationCodeRecipient();

      if (this.$refs.form) {
        this.$refs.form.validationReset();
      }
      this.language = this.currentLocale;
      this.resend_confirmed = false;
      this.modalOpened = true;
    },
    async dialogClosed() {
      // Taking in account the closing amount to avoid strange modal size glitch.
      setTimeout(() => {
        this.resetForm();
      }, 300);
    },
    confirmResend() {
      this.resend_confirmed = true;
    },
    resetForm() {
      this.clearError();
      if (this.$refs.form) {
        this.$refs.form.validationReset();
      }
      this.success = null;
      this.valid = false;
      this.recipient_name = null;
      this.recipient_email = null;
      this.add_user_email_in_cc = true;
      this.sender_name = null;
      this.personalized_message = '';
      this.language = null;
    },
    close() {
      this.$refs.dialog.close();
    },
    async loadActivationCodeRecipient() {
      if (this.activationCodeId) {
        await this.executeForm(
          {
            name: 'getActivationCodeRecipient',
            action: 'consumerGiftCard/getActivationCodeRecipientInformation',
            success: (payload) => {
              this.send_email_status = payload.send_email_status;

              if (payload.personalized_message) {
                this.personalized_message = payload.personalized_message;
              }

              if (payload.recipient_email) {
                this.recipient_email = payload.recipient_email;
              }

              if (payload.recipient_first_name) {
                this.recipient_name = payload.recipient_first_name;
              }
            },
            finally: () => {
              this.activation_code_recipient_loaded = true;
            },
          },
          {
            activation_code_id: this.activationCodeId,
          },
        );
      }
    },

    async submit() {
      if (!this.$refs.form.validate()) {
        return;
      }

      const res = await this.executeForm(
        {
          name: 'sendByEmail',
          action: 'consumerGiftCard/sendConsumerGiftCardByEmail',
        },
        {
          voucher_id: this.voucherId,
          recipient_first_name: this.recipient_name,
          recipient_email: this.recipient_email,
          sender_name: this.sender_name,
          personalized_message: this.personalized_message,
          language: this.language,
          add_user_email_in_cc: this.add_user_email_in_cc,
        },
      );

      if (!res || (res && res.errorType)) {
        this.success = false;
        return;
      }

      this.$emit('success');
      this.success = true;
    },
  },
};
</script>

<style lang="scss" scoped>
.success-box {
  text-align: left;
  font-size: var(--font-size-text-1);

  align-items: flex-start;
  display: flex;
  padding: var(--gap-xsmall);
  gap: var(--gap-small);

  background-color: rgba(var(--color-success-rgb-values), 0.1);
}
.checkbox-cc.v-input--selection-controls {
  padding-bottom: var(--gap);
  padding-top: var(--gap);
}
</style>
